import { FormEvent, useState } from 'react';

export type FormValue = string | boolean | File | null;
export type FormErrors<T> = Partial<Record<keyof T, string>>;
export type FormStatus = 'idle' | 'invalid' | 'validated';

export function useFormState<T extends Record<string, FormValue>>(
  formId: string,
  initialValues: T,
  validate: (values: T) => FormErrors<T>,
) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<FormErrors<T>>({});
  const [attempted, setAttempted] = useState(false);
  const [status, setStatus] = useState<FormStatus>('idle');

  const fieldId = (name: keyof T) => `${formId}-${String(name)}`;

  function setField<K extends keyof T>(name: K, value: T[K]) {
    const next = { ...values, [name]: value } as T;
    setValues(next);
    if (attempted) {
      const nextErrors = validate(next);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length === 0) setStatus('idle');
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setAttempted(true);
    const nextErrors = validate(values);
    setErrors(nextErrors);
    const firstInvalid = Object.keys(nextErrors)[0];
    if (firstInvalid) {
      setStatus('invalid');
      window.requestAnimationFrame(() => {
        document.getElementById(`${formId}-${firstInvalid}`)?.focus();
      });
      return;
    }
    setStatus('validated');
    window.requestAnimationFrame(() => {
      document.getElementById(`${formId}-status`)?.focus();
    });
  }

  function edit() {
    setStatus('idle');
  }

  function reset() {
    setValues(initialValues);
    setErrors({});
    setAttempted(false);
    setStatus('idle');
  }

  return { values, errors, status, fieldId, setField, handleSubmit, edit, reset };
}
